import { useState } from 'react';
import { MetricCard } from '../components/MetricCard';
import { TimeSeriesChart, type TimeSeriesData } from '../components/TimeSeriesChart';
import { RangeSelector } from '../components/RangeSelector';
import { useMockData } from '../hooks/useMockData';

export default function Network() {
  const { data, loading } = useMockData();
  const [range, setRange] = useState<'7d' | '30d' | '90d'>('30d');
  
  if (loading || !data || !data.daily_metrics.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }
  
  const latest = data.daily_metrics[0]!;
  const yesterday = data.daily_metrics[1]!;
  
  // Calculate 24h change
  const gasChange = ((latest.avg_gas_gwei - yesterday.avg_gas_gwei) / yesterday.avg_gas_gwei) * 100;
  const blockTimeChange = ((latest.avg_block_time - yesterday.avg_block_time) / yesterday.avg_block_time) * 100;
  
  // Format gas price (convert to mGwei if < 0.01)
  const gasDisplay = latest.avg_gas_gwei < 0.01 
    ? `${(latest.avg_gas_gwei * 1000).toFixed(2)} mGwei`
    : `${latest.avg_gas_gwei.toFixed(4)} Gwei`;
  
  // Filter data by range
  const rangeDays = { '7d': 7, '30d': 30, '90d': 90 }[range];
  const filteredData = data.daily_metrics.slice(0, rangeDays);
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Network Status</h1>
        <p className="text-gray-600 dark:text-gray-400">Gas prices and block times</p>
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricCard 
          title="Current Gas Price" 
          value={gasDisplay}
          change24h={gasChange}
          subtitle="Average over last 24h"
        />
        <MetricCard 
          title="Avg Block Time" 
          value={`${latest.avg_block_time}s`}
          change24h={blockTimeChange}
          subtitle="Network performance"
        />
      </div>
      
      {/* Range Selector */}
      <div className="flex justify-end">
        <RangeSelector value={range} onChange={setRange} />
      </div>
      
      {/* Gas Price Chart */}
      <TimeSeriesChart
        title={`Gas Price Trend (${rangeDays} Days)`}
        data={filteredData as unknown as TimeSeriesData[]}
        series={[
          { 
            key: 'avg_gas_gwei', 
            name: 'Gas Price', 
            color: '#10b981',
            formatter: (value) => value !== undefined ? `${value} Gwei` : 'N/A'
          }
        ]}
        yAxisLabel="Gwei"
      />
      
      {/* Block Time Chart */}
      <TimeSeriesChart
        title={`Block Time Trend (${rangeDays} Days)`}
        data={filteredData as unknown as TimeSeriesData[]}
        series={[
          { 
            key: 'avg_block_time', 
            name: 'Block Time', 
            color: '#3b82f6',
            formatter: (value) => value !== undefined ? `${value}s` : 'N/A'
          }
        ]}
        yAxisLabel="Seconds"
      />
    </div>
  );
}
